import {
  Box,
  Checkbox,
  FormControl,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useParams } from 'react-router-dom';

const MENU_PROPS = { PaperProps: { style: { maxHeight: 48 * 4.5 + 8, width: 250 } } };

/**
 * TestSuiteSelector
 *
 * Shared across AiInputScreen (all source modes) and JiraStorySelector.
 *
 * Props:
 *   testSuites        — array of { id, name, status }
 *   selectedTestSuites — array of selected suite IDs
 *   onTestSuitesChange — (ids: string[]) => void
 *   projectId         — used for "Add test suite" link; falls back to useParams
 */
const TestSuiteSelector = ({ testSuites = [], selectedTestSuites = [], onTestSuitesChange, projectId: propProjectId }) => {
  const { projectId: paramProjectId } = useParams();
  const projectId = propProjectId ?? paramProjectId;

  const handleChange = (e) => {
    const val = e.target.value;
    onTestSuitesChange(typeof val === 'string' ? val.split(',') : val);
  };

  return (
    <Box>
      <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
        Test Suite <Box component="span" sx={{ color: 'error.main' }}>*</Box>
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
        Applies to all generated test cases
      </Typography>
      <FormControl fullWidth>
        <Select
          multiple
          displayEmpty
          size="small"
          value={selectedTestSuites}
          onChange={handleChange}
          input={<OutlinedInput />}
          IconComponent={KeyboardArrowDownIcon}
          inputProps={{ 'aria-label': 'Select test suites' }}
          renderValue={(selected) =>
            selected.length === 0
              ? <Typography component="span" variant="body2" color="text.disabled">Select test suite(s)</Typography>
              : testSuites.filter((s) => selected.includes(s.id)).map((s) => s.name).join(', ')
          }
          MenuProps={MENU_PROPS}
        >
          {testSuites.map((suite) => (
            <MenuItem key={suite.id} value={suite.id} disabled={suite.status === 'suspended'}>
              <Checkbox disableRipple checked={selectedTestSuites.includes(suite.id)} disabled={suite.status === 'suspended'} />
              <ListItemText
                primary={
                  <>
                    {suite.name}
                    {suite.status === 'suspended' && (
                      <Box component="span" sx={{ fontSize: '10px', color: 'error.main', ml: 0.5 }}>(Suspended)</Box>
                    )}
                  </>
                }
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Box
        component="a"
        href={`/project/${projectId}/testsuites`}
        target="_blank"
        rel="noopener noreferrer"
        sx={{ fontSize: 13, color: 'primary.main', textDecoration: 'none', display: 'inline-block', mt: 0.5 }}
      >
        + Add test suite
      </Box>
    </Box>
  );
};

export default TestSuiteSelector;